import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ArsConsultNoteImportDetail } from 'app/core/models/ars-consult-note-import.model';
import { PdfFile } from 'app/core/interfaces/pdf-file';
import { Office } from 'app/core/models/office.model';
import { ArsConsultNoteImportsService } from 'app/core/services/ars-consult-note-imports.service';
import { PdfBasicViewerComponent } from '../../../shared/components/pdf-basic-viewer/pdf-basic-viewer.component';
import { PatientNameComponent } from '../../../shared/components/patient-name/patient-name.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';
import { OfficeComponent } from '../../../shared/components/office/office.component';
import { OfficeFaxSelectComponent } from '../../../shared/components/office-fax-select/office-fax-select.component';
import { JoinListPipe } from '../../../shared/pipes/join-list.pipe';
import { ReplacePipe } from 'app/shared/pipes/replace.pipe';

@Component({
  selector: 'app-capture-admin-ars-consult-note-import',
  templateUrl: './capture-admin-ars-consult-note-import.component.html',
  styleUrls: ['./capture-admin-ars-consult-note-import.component.scss'],
  imports: [
    FormsModule,
    RouterLink,
    MatProgressSpinner,
    PdfBasicViewerComponent,
    PatientNameComponent,
    PrescriberNameComponent,
    OfficeComponent,
    OfficeFaxSelectComponent,
    DatePipe,
    JoinListPipe,
    ReplacePipe,
    TitleCasePipe
  ]
})
export class CaptureAdminArsConsultNoteImportComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private arsConsultNoteImportsService = inject(ArsConsultNoteImportsService);

  loading = true;
  activeTab: 'consultNote' | 'ocr' = 'consultNote';
  arsConsultNoteImport: ArsConsultNoteImportDetail;
  pdfFile: PdfFile;
  selectedOffice: Office = null;
  assigningOffice = false;
  assignOfficeError: string = null;
  noteCreatedOnAssign = false;
  restarting = false;
  restartError: string = null;
  restartedStep: 'ocr' | 'llm' = null;

  ngOnInit() {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    this.arsConsultNoteImportsService.get(id).subscribe(arsConsultNoteImport => {
      this.arsConsultNoteImport = arsConsultNoteImport;
      this.pdfFile = arsConsultNoteImport.url ? this.buildPdfFile(arsConsultNoteImport) : null;
      this.loading = false;
    });
  }

  // only set when the llm extracted at least one office value
  get llmResponse() {
    const response = this.arsConsultNoteImport?.llmResponse;
    const hasOfficeText = response &&
      [response.officeName, response.officeAddress, response.officePhone, response.officeFax].some(value => value);

    return hasOfficeText ? response : null;
  }

  onRestartClick(step: 'ocr' | 'llm') {
    this.restarting = true;
    this.restartError = null;

    this.arsConsultNoteImportsService.restart(this.arsConsultNoteImport.id, step).subscribe({
      next: arsConsultNoteImport => {
        this.arsConsultNoteImport = arsConsultNoteImport;
        this.restartedStep = step;
        this.restarting = false;
      },
      error: (error: HttpErrorResponse) => {
        this.restartError = error.error?.errors?.join(' ') || 'Processing could not be restarted.';
        this.restarting = false;
      }
    });
  }

  onAssignOfficeSubmit() {
    if (!this.selectedOffice) {
      return;
    }

    this.assigningOffice = true;
    this.assignOfficeError = null;

    this.arsConsultNoteImportsService.assignOffice(this.arsConsultNoteImport.id, this.selectedOffice.id).subscribe({
      next: arsConsultNoteImport => {
        // assigning creates the note when the import was waiting on details
        this.noteCreatedOnAssign = arsConsultNoteImport.status === 'finished';
        this.arsConsultNoteImport = arsConsultNoteImport;
        this.assigningOffice = false;
      },
      error: (error: HttpErrorResponse) => {
        this.assignOfficeError = error.error?.errors?.join(' ') || 'The office could not be assigned.';
        this.assigningOffice = false;
      }
    });
  }

  private buildPdfFile(arsConsultNoteImport: ArsConsultNoteImportDetail): PdfFile {
    return {
      id: arsConsultNoteImport.id,
      url: arsConsultNoteImport.url,
      fileCreatedAt: null,
      annotations: null,
      fileExpirableDownloadPath: null
    };
  }
}
